import { useEffect, useMemo, useState } from 'react'
import { core } from '@musinsa/pbo-core'
import { Icon as DsIcon } from '@musinsa/pbo-assets'
import './tableGroup.css'

/** 분할 패널 안 표 헤더 고정 위치 — 액션바 높이(40) + 액션바·표 간격(8). GR09 생성본과 같은 값. */
const TABLE_STICKY_OFFSET = 48
/** NO 컬럼 최소 폭 — 생성본 공통 값. 한 자리 번호는 이 폭을 쓴다. */
const NO_COLUMN_MIN_WIDTH = 48
/** 셀 좌우 패딩 합(pbo-core 표 cellPaddingInline 12 × 2) + 여유 8. */
const NO_COLUMN_CHROME_WIDTH = 32
/** 14px 숫자 한 자리 폭에 여유를 둔 값. */
const NO_COLUMN_DIGIT_WIDTH = 9

/**
 * NO 컬럼 폭 — 표시할 가장 큰 번호의 자릿수에 맞춘다.
 * 48 고정이면 세 자리부터 '1…' 로 잘렸다(LOGISPRD-17319). 상세 표는 페이지 시작 번호가 붙어 다섯 자리까지 간다.
 */
export const noColumnWidth = (lastNo: number): number =>
  Math.max(NO_COLUMN_MIN_WIDTH, NO_COLUMN_CHROME_WIDTH + String(Math.max(1, lastNo)).length * NO_COLUMN_DIGIT_WIDTH)
/** NO 컬럼 키. 행 데이터에 없는 이름이라 값은 render 가 채운다. */
const NO_COLUMN_KEY = '__no'

type ColumnPin = 'left' | 'right' | 'none'

/**
 * 표 헤더 조작(드래그 순서변경·고정·[컬럼] 탭 숨김) 결과에서 NO 를 맨 앞·왼쪽 고정·표시로 되돌린다.
 * 폭 조절 핸들 때문에 NO 에 dataIndex 를 주면 pbo-core 가 NO 도 일반 데이터 컬럼처럼 옮기고 숨긴다.
 * 컬럼 정의만으로는 막을 수 없어 세 값을 제어형으로 들고 바뀔 때마다 NO 를 제자리에 둔다.
 */
export const keepNoFirst = (order: string[]): string[] => [NO_COLUMN_KEY, ...order.filter((id) => id !== NO_COLUMN_KEY)]
export const keepNoPinned = (pinned: Record<string, ColumnPin>): Record<string, ColumnPin> => ({ ...pinned, [NO_COLUMN_KEY]: 'left' })
export const keepNoVisible = (hidden: string[]): string[] => hidden.filter((id) => id !== NO_COLUMN_KEY)
const LIST_SEARCH_WIDTH = 240
const TOOLBAR_DIVIDER_STYLE = { height: 18, margin: '0 4px', borderLeft: '1px solid #d9d9d9', display: 'block', flex: 'none' } as const

export interface TableGroupPager {
  page: number
  pageSize: number
  pageSizeOptions: string[]
  onPage: (page: number, pageSize: number) => void
}

interface Props<T extends object> {
  columns: core.CoreColumnType<T>[]
  rows: T[]
  total: number
  loading?: boolean
  /** 서버 페이징 표만 넘긴다. NO 컬럼이 페이지 시작 번호부터 센다. */
  pager?: TableGroupPager
  downloadDisabled?: boolean
  onDownload: () => void
  onRowDoubleClick?: (row: T) => void
  isPicked?: (row: T) => boolean
  emptyText?: string
}

const matchesKeyword = <T extends object>(row: T, keyword: string, columns: core.CoreColumnType<T>[]) =>
  columns.some((column) => {
    if (column.dataIndex == null) return false
    const value = (row as Record<string, unknown>)[String(column.dataIndex)]
    return value != null && String(value).toLowerCase().includes(keyword)
  })

/**
 * 일마감조회 2화면(RP12·RP13) 표 그룹. GR09 입고조회 2.0 생성본의 TableGroup 구조를 따른다.
 *
 * 액션바(전체 건수 / 페이저·목록 검색·엑셀 다운로드) 아래 `core.Table` 을 둔다. NO 컬럼을 앞에 붙이고,
 * 목록 검색은 현재 받아 둔 행만 거른다. 조회 결과가 바뀌면 검색어를 비운다.
 */
export function TableGroup<T extends object>({
  columns,
  rows,
  total,
  loading,
  pager,
  downloadDisabled,
  onDownload,
  onRowDoubleClick,
  isPicked,
  emptyText,
}: Readonly<Props<T>>) {
  const [keyword, setKeyword] = useState('')
  useEffect(() => setKeyword(''), [rows])
  const [columnOrder, setColumnOrder] = useState<string[]>([])
  const [pinnedColumns, setPinnedColumns] = useState<Record<string, ColumnPin>>(() => keepNoPinned({}))
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([])

  const rowKeys = useMemo(() => new Map(rows.map((row, index) => [row, String(index)])), [rows])
  const lowered = keyword.trim().toLowerCase()
  const isSearching = lowered !== ''
  const visibleRows = useMemo(
    () => (isSearching ? rows.filter((row) => matchesKeyword(row, lowered, columns)) : rows),
    [columns, isSearching, lowered, rows],
  )

  const startNo = pager ? (pager.page - 1) * pager.pageSize : 0
  // 목록 검색으로 줄어도 폭은 받아 둔 행 기준으로 둔다 — 검색어를 칠 때마다 표 폭이 흔들리지 않게.
  const noWidth = noColumnWidth(startNo + rows.length)
  const tableColumns = useMemo<core.CoreColumnType<T>[]>(
    () => [
      {
        title: 'NO',
        key: NO_COLUMN_KEY,
        // pbo-core 표는 dataIndex 가 있는 컬럼에만 폭 조절 핸들을 단다. 행에 없는 키라 값은 render 로 채운다.
        dataIndex: NO_COLUMN_KEY as keyof T & string,
        width: noWidth,
        align: 'center',
        // dataIndex 를 주면 정렬 대상이 되고 자동 왼쪽 고정이 풀린다. 정렬은 여기서 끄고,
        // 왼쪽 고정·순서·숨김은 아래 표의 제어형 값(keepNo*)이 지킨다.
        sortable: false,
        render: (_value: unknown, _row: T, index: number) => startNo + index + 1,
      },
      ...columns,
    ],
    [columns, noWidth, startNo],
  )

  return (
    <core.Flex vertical gap={8} align="stretch">
      <core.Flex className="pbo-v2-actionbar-sticky" style={{ padding: '4px 0' }} gap={8} justify="space-between" align="center">
        <core.Space>
          {/* 목록 검색은 받아 둔 행만 거르므로, 검색 중에는 걸러진 건수를 보이고 서버 기준 페이저는 숨긴다. */}
          <core.TableStat total={isSearching ? visibleRows.length : total} />
        </core.Space>
        <core.Space>
          {pager && !isSearching && (
            <core.TablePager
              variant="paged"
              page={pager.page}
              pageSize={pager.pageSize}
              total={total}
              pageSizeOptions={pager.pageSizeOptions}
              onPage={pager.onPage}
            />
          )}
          <core.Input.Search
            placeholder="목록 검색"
            allowClear
            style={{ width: LIST_SEARCH_WIDTH }}
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            onSearch={(value) => setKeyword((value ?? '').trim())}
          />
          <span style={TOOLBAR_DIVIDER_STYLE} />
          <core.Button icon={<DsIcon name="ic-download" size={16} color="currentColor" />} disabled={downloadDisabled} onClick={onDownload}>
            엑셀 다운로드
          </core.Button>
        </core.Space>
      </core.Flex>
      <core.Table<T>
        columns={tableColumns}
        dataSource={visibleRows}
        rowKey={(row) => rowKeys.get(row) ?? String(rows.indexOf(row))}
        loading={loading}
        size="middle"
        scroll={{ x: 'max-content' }}
        pagination={false}
        stickyOffset={TABLE_STICKY_OFFSET}
        columnOrder={columnOrder}
        onColumnOrderChange={(order) => setColumnOrder(keepNoFirst(order))}
        pinnedColumns={pinnedColumns}
        onPinnedColumnsChange={(pinned) => setPinnedColumns(keepNoPinned(pinned))}
        hiddenColumns={hiddenColumns}
        onHiddenColumnsChange={(hidden) => setHiddenColumns(keepNoVisible(hidden))}
        onRow={onRowDoubleClick ? (row) => ({ onDoubleClick: () => onRowDoubleClick(row) }) : undefined}
        rowClassName={(row) => (isPicked?.(row) ? 'pbo-v2-row-picked' : '')}
        locale={emptyText ? { emptyText: <core.Empty image={core.Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} /> } : undefined}
      />
    </core.Flex>
  )
}
